import { sslFormSchema } from '@constants';
import {
  ApiEndpoint,
  columnDirection,
  FormActionType,
  FormType,
  InputType,
  ServiceModalName,
  sslCertificateType,
  Translation,
} from '@enums';
import i18n from '@translations';
import type { DictionaryOptionItem, EndpointType, FormConfig, FormConfigMapping, Options } from '@types';

interface GetSSLFormConfigParams {
  endpoint?: EndpointType;
  isSubmitting?: boolean;
  isEdit?: boolean;
  isLetsEncrypt?: boolean;
  SSLTypeOption?: DictionaryOptionItem[];
  normalDomains?: Options;
}

export const getSSLFormConfig = ({
  endpoint = ApiEndpoint.DEFAULT,
  isSubmitting = false,
  isEdit = false,
  isLetsEncrypt = false,
  SSLTypeOption = [],
  normalDomains = [],
}: GetSSLFormConfigParams): FormConfig => {
  const tModal: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:modals.ssl.${key}`);

  const tButton: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:buttons.ssl.${key}`);

  const tForm: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:forms.ssl.${key}`);

  const SSL_FORM: FormConfigMapping = {
    [ApiEndpoint.DEFAULT]: {
      formType: FormType.CdnSSL,
      endpoint: ApiEndpoint.DEFAULT,
      formActionType: isEdit ? FormActionType.Edit : FormActionType.Create,
      modalName: ServiceModalName.FormModal,
      label: {
        title: tModal(`${isEdit ? 'editCertificate' : 'addCertificate'}`),
        description: tModal(`${isEdit ? 'editSSLDescription' : 'addNewSSLDescription'}`),
        confirmButton: tButton(`${isEdit ? 'editSSL' : 'addSSL'}`),
      },
      inputList: [
        {
          name: 'name',
          defaultValue: '',
          inputType: InputType.Text,
          required: true,
          placeholder: tForm('enterYourCertificateName'),
          label: tForm('certificateName'),
          direction: columnDirection.Row,
          disabled: isSubmitting || (isEdit && isLetsEncrypt),
        },
        {
          name: 'type',
          defaultValue: sslCertificateType.manual,
          inputType: InputType.Select,
          required: true,
          placeholder: tForm('selectYourSSLType'),
          label: tForm('sslType'),
          direction: columnDirection.Row,
          disabled: isEdit,
          options: SSLTypeOption,
        },
        {
          name: 'public_key',
          defaultValue: '',
          inputType: InputType.Area,
          required: true,
          placeholder: tForm('enterYourPublicKey'),
          label: tForm('publicKey'),
          direction: columnDirection.Row,
          disabled: isSubmitting,
          fullWidth: true,
          textInputClassName: 'padded-scrollbar-track',
          uploadLabel: tForm('uploadPublicKeyFile'),
          hide: {
            field: 'type',
            condition: (value: string) => value === 'auto',
          },
        },
        {
          name: 'private_key',
          defaultValue: '',
          inputType: InputType.Area,
          required: true,
          placeholder: tForm('enterYourPrivateKey'),
          label: tForm('privateKey'),
          direction: columnDirection.Row,
          disabled: isSubmitting,
          fullWidth: true,
          textInputClassName: 'padded-scrollbar-track',
          uploadLabel: tForm('uploadPrivateKeyFile'),
          hide: {
            field: 'type',
            condition: (value: string) => value === 'auto',
          },
        },
        {
          name: 'domain',
          defaultValue: '',
          emptyData: '',
          inputType: InputType.MultipleSelectInput,
          placeholder: tForm('enterYourDomainNameList'),
          label: tForm('domainNameList'),
          direction: columnDirection.Row,
          disabled: isSubmitting || (isEdit && isLetsEncrypt),
          options: normalDomains,
          maxSelections: 5,
          hide: {
            field: 'type',
            condition: (value: string) => value === 'manual',
          },
        },
      ],
      validationSchema: sslFormSchema,
    },
  };

  return (SSL_FORM[endpoint] ?? SSL_FORM[ApiEndpoint.DEFAULT]) as FormConfig;
};
